import React, { useState } from 'react';
import { FaCreditCard, FaTimes } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { checkoutPhotoCart } from '../services/api';

const PAYMENT_METHODS = {
  visa: 'Carte Visa',
  wave_ci: "Wave Côte d'Ivoire",
  orange_money_ci: "Orange Money Côte d'Ivoire",
};

const getPhotoDisplayName = (photo) => {
  const name = photo?.name || photo?.title || photo?.filename || `Photo ${photo?.id || ''}`;
  return String(name).split('/').pop() || name;
};

const CartPaymentModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const { items: cartItems, clearCart } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentData, setPaymentData] = useState({
    method: 'visa',
    cardholderName: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
    billingEmail: user?.email || '',
    mobileNumber: '',
  });

  if (!isOpen) return null;

  const cartPhotoIds = cartItems.map(item => item.id);
  const cartTotal = cartItems.reduce((sum, photo) => sum + Number(photo.price || 0), 0);
  const activePaymentLabel = PAYMENT_METHODS[paymentData.method] || PAYMENT_METHODS.visa;
  const waveQrPayload = `WAVE-CI|amount:${cartTotal.toFixed(2)}|items:${cartPhotoIds.length}|phone:${paymentData.mobileNumber || 'pending'}`;
  const waveQrCells = Array.from({ length: 81 }, (_, index) => {
    const code = waveQrPayload.charCodeAt(index % waveQrPayload.length) || 0;
    const row = Math.floor(index / 9);
    const col = index % 9;
    const inCorner = (row < 3 && col < 3) || (row < 3 && col > 5) || (row > 5 && col < 3);
    return inCorner || ((code + index + row * col) % 3 === 0);
  });

  const handlePaymentChange = (field, value) => {
    setPaymentData(prev => ({ ...prev, [field]: value }));
    setPaymentError('');
  };

  const validatePaymentForm = () => {
    if (paymentData.method === 'wave_ci' || paymentData.method === 'orange_money_ci') {
      const digits = paymentData.mobileNumber.replace(/\D/g, '');
      if (digits.length < 8 || digits.length > 14) return 'Saisissez un numéro mobile money valide.';
      return '';
    }

    const digits = paymentData.cardNumber.replace(/\D/g, '');
    const cvcDigits = paymentData.cvc.replace(/\D/g, '');
    const expiryMatch = paymentData.expiry.match(/^(0[1-9]|1[0-2])\/\d{2}$/);

    if (!paymentData.cardholderName.trim()) return 'Saisissez le nom du titulaire.';
    if (digits.length < 13 || digits.length > 19) return 'Saisissez un numéro de carte valide.';
    if (!expiryMatch) return "Utilisez MM/AA pour la date d'expiration.";
    if (cvcDigits.length < 3 || cvcDigits.length > 4) return 'Saisissez un CVC valide.';
    if (!paymentData.billingEmail.trim() || !paymentData.billingEmail.includes('@')) return 'Saisissez un e-mail de facturation valide.';

    return '';
  };

  const handleCheckoutCart = async (e) => {
    e.preventDefault();

    if (cartPhotoIds.length === 0) {
      setPaymentError('Votre panier est vide');
      return;
    }

    const validationError = validatePaymentForm();
    if (validationError) {
      setPaymentError(validationError);
      return;
    }

    setCheckingOut(true);
    setPaymentError('');
    try {
      const purchasedPhotoIds = [...cartPhotoIds];
      const methodLabel = PAYMENT_METHODS[paymentData.method] || 'paiement';

      await checkoutPhotoCart(purchasedPhotoIds, {
        method: paymentData.method,
        mobile_number: paymentData.mobileNumber,
        billing_email: paymentData.billingEmail,
      });

      clearCart();
      window.dispatchEvent(new CustomEvent('photomatch:cart-checkout-success', {
        detail: { photoIds: purchasedPhotoIds, methodLabel },
      }));
      setPaymentData(prev => ({
        ...prev,
        cardNumber: '',
        expiry: '',
        cvc: '',
        mobileNumber: '',
      }));
      onSuccess?.(purchasedPhotoIds, methodLabel);
      onClose();
    } catch (err) {
      setPaymentError(err.message || 'Le paiement a échoué');
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={() => !checkingOut && onClose()}
    >
      <form
        onSubmit={handleCheckoutCart}
        style={{
          width: '100%',
          maxWidth: '520px',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'white',
          borderRadius: '14px',
          padding: '1.5rem',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--primary-orange)' }}>Paiement</h3>
            <p style={{ margin: '0.25rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
              {cartPhotoIds.length} photo{cartPhotoIds.length !== 1 ? 's' : ''} · {cartTotal.toFixed(2)} F
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={checkingOut}
            style={{
              border: 'none',
              background: 'var(--off-white)',
              color: 'var(--dark-gray)',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              cursor: checkingOut ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FaTimes />
          </button>
        </div>

        <div style={{
          padding: '0.85rem',
          background: 'var(--off-white)',
          borderRadius: '10px',
          marginBottom: '1rem',
          display: 'grid',
          gap: '0.5rem',
        }}>
          {cartItems.map(photo => (
            <div key={photo.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', color: 'var(--dark-gray)', fontSize: '0.9rem' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getPhotoDisplayName(photo)}
              </span>
              <strong>{Number(photo.price || 0).toFixed(2)} F</strong>
            </div>
          ))}
        </div>

        {paymentError && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>{paymentError}</div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
          {Object.entries(PAYMENT_METHODS).map(([method, label]) => (
            <button
              key={method}
              type="button"
              onClick={() => handlePaymentChange('method', method)}
              style={{
                border: paymentData.method === method ? '2px solid var(--primary-orange)' : '1px solid var(--light-gray)',
                background: paymentData.method === method ? 'rgba(255,122,0,0.08)' : 'white',
                color: 'var(--dark-gray)',
                borderRadius: '8px',
                padding: '0.75rem 0.5rem',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '0.82rem',
                minHeight: '54px',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {paymentData.method === 'visa' && (
          <>
            <div className="form-group">
              <label>Nom du titulaire</label>
              <input
                type="text"
                value={paymentData.cardholderName}
                onChange={e => handlePaymentChange('cardholderName', e.target.value)}
                placeholder="Nom sur la carte"
                autoComplete="cc-name"
              />
            </div>

            <div className="form-group">
              <label>Numéro de carte</label>
              <input
                type="text"
                inputMode="numeric"
                value={paymentData.cardNumber}
                onChange={e => handlePaymentChange('cardNumber', e.target.value.replace(/[^\d ]/g, '').slice(0, 23))}
                placeholder="4242 4242 4242 4242"
                autoComplete="cc-number"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                  <label>Expiration</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={paymentData.expiry}
                  onChange={e => handlePaymentChange('expiry', e.target.value.replace(/[^\d/]/g, '').slice(0, 5))}
                  placeholder="MM/YY"
                  autoComplete="cc-exp"
                />
              </div>
              <div className="form-group">
                <label>CVC</label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={paymentData.cvc}
                  onChange={e => handlePaymentChange('cvc', e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="123"
                  autoComplete="cc-csc"
                />
              </div>
            </div>

            <div className="form-group">
              <label>E-mail de facturation</label>
              <input
                type="email"
                value={paymentData.billingEmail}
                onChange={e => handlePaymentChange('billingEmail', e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
          </>
        )}

        {(paymentData.method === 'wave_ci' || paymentData.method === 'orange_money_ci') && (
          <>
            <div className="form-group">
              <label>Numéro {activePaymentLabel}</label>
              <input
                type="tel"
                inputMode="tel"
                value={paymentData.mobileNumber}
                onChange={e => handlePaymentChange('mobileNumber', e.target.value.replace(/[^\d+ ]/g, '').slice(0, 18))}
                placeholder="+225 07 00 00 00 00"
                autoComplete="tel"
              />
            </div>

            {paymentData.method === 'wave_ci' && (
              <div style={{
                border: '1px solid var(--light-gray)',
                borderRadius: '10px',
                padding: '1rem',
                marginBottom: '1rem',
                display: 'grid',
                gridTemplateColumns: '112px 1fr',
                gap: '1rem',
                alignItems: 'center',
                background: '#f8fbff',
              }}>
                <div style={{
                  width: '112px',
                  height: '112px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(9, 1fr)',
                  gridTemplateRows: 'repeat(9, 1fr)',
                  gap: '3px',
                  padding: '8px',
                  background: 'white',
                  border: '1px solid #d8e6f8',
                }}>
                  {waveQrCells.map((active, index) => (
                    <span
                      key={index}
                      style={{
                        background: active ? '#111827' : '#fff',
                        borderRadius: '2px',
                      }}
                    />
                  ))}
                </div>
                <div>
                  <strong style={{ color: 'var(--dark-gray)' }}>QR code Wave</strong>
                  <p style={{ margin: '0.35rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
                    Scannez ce code dans Wave pour payer {cartTotal.toFixed(2)} F.
                  </p>
                </div>
              </div>
            )}

            {paymentData.method === 'orange_money_ci' && (
              <div style={{
                border: '1px solid #ffd2a8',
                borderRadius: '10px',
                padding: '1rem',
                marginBottom: '1rem',
                background: '#fff7ed',
                color: 'var(--dark-gray)',
              }}>
                <strong>Message de paiement Orange Money</strong>
                <p style={{ margin: '0.35rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
                  Une demande de paiement de {cartTotal.toFixed(2)} F sera envoyée à {paymentData.mobileNumber || 'votre numéro Orange'}.
                </p>
              </div>
            )}
          </>
        )}

        <button
          type="submit"
          disabled={checkingOut}
          className="btn-submit"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.45rem',
          }}
        >
          <FaCreditCard /> {checkingOut ? 'Traitement du paiement...' : `Payer ${cartTotal.toFixed(2)} F avec ${activePaymentLabel}`}
        </button>
      </form>
    </div>
  );
};

export default CartPaymentModal;
